// Adds the cable takeoff (Kabelgruppen / cables-beta) to the Excel export as two
// worksheets, structured the way a German QS builds a tender:
//   • "Kabelmengen"  — LV quantities: total metres per cable type, grouped by
//     installation system (Los: Starkstrom / BMA / KNX / Medien / Erdung …).
//   • "Kabel Detail" — the schedule: one row per circuit (plan · board · cable
//     type · devices · length · method) for verification.
// The rows come from buildCableExportRows — the run's estimate with the
// estimator's edits applied — NOT from the published quantities alone.

import ExcelJS from "exceljs";
import { systemMeta } from "../CableTakeoffTabV3/cableSystems";
import type { CableExportRow } from "./buildCableExportRows";
import { roundMeters } from "./utils";

const SECTION_BG = "FFD6E4F7";
const SECTION_FONT = "FF003A8C";
const HEADER_FONT = "FF006400";
const HEADER_BG = "FFE6FFEF";
const TOTAL_BG = "FFF3F4F6";
const STRUCK_FONT = "FF94A3B8";

export interface CableExportLabels {
  /** Sheet names. */
  quantitiesSheet: string; // "Kabelmengen"
  detailSheet: string; // "Kabel Detail"
  /** Localised system (Los) name from a system key. */
  systemLabel: (systemKey: string) => string;
  /** Column headers. */
  cableType: string;
  circuits: string;
  devices: string;
  length: string;
  method: string;
  plan: string;
  board: string;
  circuit: string;
  measured: string;
  vertical: string;
  status: string;
  /** Row labels. */
  subtotal: (systemLabel: string) => string; // "{system} — Summe"
  grandTotal: string; // "Gesamt"
  /** What happened to a row that no longer carries the run's own estimate. */
  statusStruck: string;
  statusEdited: string;
  statusEditsUnavailable: string;
}

const round1 = (metres: number): number => Math.round(metres * 10) / 10;

/** Special systems first (the exceptions), Starkstrom (power) last (the bulk). */
const orderedSystems = (keys: string[]): string[] =>
  [...keys].sort((a, b) => (a === "power" ? 1 : 0) - (b === "power" ? 1 : 0));

const sectionHeader = (
  sheet: ExcelJS.Worksheet,
  text: string,
  span: number,
): void => {
  const rowNum = (sheet.lastRow?.number ?? 0) + 1;
  const row = sheet.getRow(rowNum);
  sheet.mergeCells(rowNum, 1, rowNum, span);
  const cell = row.getCell(1);
  cell.value = text;
  cell.font = { bold: true, color: { argb: SECTION_FONT } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: SECTION_BG },
  } as ExcelJS.Fill;
  cell.alignment = { vertical: "middle", horizontal: "left" };
  row.height = 18;
  row.commit();
};

const headerRow = (
  sheet: ExcelJS.Worksheet,
  values: Record<string, string>,
): void => {
  const row = sheet.addRow(values);
  row.height = 20;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: HEADER_FONT } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: HEADER_BG },
    } as ExcelJS.Fill;
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = { bottom: { style: "thin", color: { argb: "FFBFBFBF" } } };
  });
  row.commit();
};

const boldRow = (row: ExcelJS.Row, bg?: string): void => {
  row.eachCell((cell) => {
    cell.font = { bold: true };
    if (bg)
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bg },
      } as ExcelJS.Fill;
  });
  row.commit();
};

const struckRow = (row: ExcelJS.Row): void => {
  row.eachCell((cell) => {
    cell.font = { strike: true, color: { argb: STRUCK_FONT } };
  });
  row.commit();
};

const groupBySystem = (
  rows: CableExportRow[],
): Map<string, CableExportRow[]> => {
  const bySystem = new Map<string, CableExportRow[]>();
  for (const row of rows) {
    const key = systemMeta(row.system).key;
    const group = bySystem.get(key) ?? [];
    group.push(row);
    bySystem.set(key, group);
  }
  return bySystem;
};

const statusLabel = (
  row: CableExportRow,
  labels: CableExportLabels,
): string => {
  if (row.status === "struck") return labels.statusStruck;
  if (row.status === "edited") return labels.statusEdited;
  if (row.status === "editsUnavailable") return labels.statusEditsUnavailable;
  return "";
};

const buildQuantitiesSheet = (
  workbook: ExcelJS.Workbook,
  rows: CableExportRow[],
  labels: CableExportLabels,
): void => {
  const sheet = workbook.addWorksheet(labels.quantitiesSheet);
  sheet.columns = [
    { key: "CableType", width: 34 },
    { key: "Circuits", width: 14 },
    { key: "Devices", width: 12 },
    { key: "Length", width: 14 },
    { key: "Unit", width: 8 },
  ];
  const header = {
    CableType: labels.cableType,
    Circuits: labels.circuits,
    Devices: labels.devices,
    Length: labels.length,
    Unit: "",
  };

  // A struck circuit is fed from nowhere: it carries 0 m and is not one of the
  // circuits being tendered, so it is listed in the schedule, not counted here.
  const counted = rows.filter((row) => row.status !== "struck");
  const bySystem = groupBySystem(counted);
  let grand = 0;
  for (const system of orderedSystems([...bySystem.keys()])) {
    const systemRows = bySystem.get(system)!;
    sectionHeader(sheet, labels.systemLabel(system), 5);
    headerRow(sheet, header);

    const byType = new Map<
      string,
      { metres: number; circuits: number; devices: number }
    >();
    for (const row of systemRows) {
      const type = row.cableType || row.circuit || "—";
      const entry = byType.get(type) ?? { metres: 0, circuits: 0, devices: 0 };
      entry.metres += row.lengthM;
      entry.circuits += 1;
      entry.devices += row.deviceCount;
      byType.set(type, entry);
    }
    let subtotal = 0;
    for (const [type, entry] of byType) {
      const metres = roundMeters(entry.metres);
      subtotal += metres;
      sheet.addRow({
        CableType: type,
        Circuits: entry.circuits,
        Devices: entry.devices || "",
        Length: metres,
        Unit: "m",
      });
    }
    grand += subtotal;
    boldRow(
      sheet.addRow({
        CableType: labels.subtotal(labels.systemLabel(system)),
        Length: roundMeters(subtotal),
        Unit: "m",
      }),
    );
  }
  boldRow(
    sheet.addRow({
      CableType: labels.grandTotal,
      Length: roundMeters(grand),
      Unit: "m",
    }),
    TOTAL_BG,
  );
};

const buildDetailSheet = (
  workbook: ExcelJS.Workbook,
  rows: CableExportRow[],
  labels: CableExportLabels,
): void => {
  const sheet = workbook.addWorksheet(labels.detailSheet);
  sheet.columns = [
    { key: "Plan", width: 24 },
    { key: "Board", width: 24 },
    { key: "Circuit", width: 40 },
    { key: "CableType", width: 24 },
    { key: "Devices", width: 10 },
    { key: "Length", width: 12 },
    { key: "Method", width: 16 },
    { key: "Measured", width: 14 },
    { key: "Vertical", width: 14 },
    { key: "Status", width: 34 },
  ];
  const header = {
    Plan: labels.plan,
    Board: labels.board,
    Circuit: labels.circuit,
    CableType: labels.cableType,
    Devices: labels.devices,
    Length: labels.length,
    Method: labels.method,
    Measured: labels.measured,
    Vertical: labels.vertical,
    Status: labels.status,
  };

  const bySystem = groupBySystem(rows);
  for (const system of orderedSystems([...bySystem.keys()])) {
    const systemRows = bySystem.get(system)!;
    sectionHeader(sheet, labels.systemLabel(system), 10);
    headerRow(sheet, header);
    for (const row of systemRows) {
      const added = sheet.addRow({
        Plan: row.planName,
        Board: row.board,
        Circuit: row.circuit,
        CableType: row.cableType,
        Devices: row.deviceCount || "",
        Length: roundMeters(row.lengthM),
        Method: row.method,
        Measured: row.measuredM === null ? "" : round1(row.measuredM),
        Vertical: row.verticalM === null ? "" : round1(row.verticalM),
        Status: statusLabel(row, labels),
      });
      // Struck through rather than dropped: a circuit that silently vanishes
      // leaves a hole nobody can reconcile against the plan.
      if (row.status === "struck") struckRow(added);
    }
  }
};

/** Add both cable worksheets to an existing workbook. No-op if there are no rows. */
export const addCableSheets = (
  workbook: ExcelJS.Workbook,
  rows: CableExportRow[],
  labels: CableExportLabels,
): void => {
  if (rows.length === 0) return;
  buildQuantitiesSheet(workbook, rows, labels);
  buildDetailSheet(workbook, rows, labels);
};
